// The X thread as the publisher builds it, so the preview shows the tweets X will get.
// Mirror of marabot-api src/third-party-api/ayrshare/ayrshare.service.ts (the Twitter
// branch of publishPost, steps 1-6) and x-thread-sentences.util.ts, with the abbreviation
// lists of openai/commercial-post-processing.ts - change both sides together.

const ALWAYS = new Set([
    "sq", "approx", "no", "min", "max", "avg",
    "ste", "apt", "rt", "rte", "st", "mt", "bldg", "fl",
    "mr", "mrs", "ms", "dr", "jr", "prof", "vs", "e.g", "i.e", "dept", "mgmt", "assn", "u.s",
]);
const TRAILING = new Set([
    "ft", "sf", "ac", "yd", "mi", "cu", "lb", "lbs", "oz", "kw", "hp", "yr", "yrs", "mo", "mos", "a.m", "p.m",
    "ave", "blvd", "rd", "ln", "ct", "pl", "cir", "pkwy", "hwy", "trl", "ter",
    "inc", "co", "corp", "ltd", "llc", "lp", "llp", "etc", "est", "ext", "sr",
]);
const LEASE_TERM = new Set(["nnn", "nn"]);

const TWEET_LIMIT = 275;

const periodContinues = (fragment: string, next: string): boolean => {
    const leadIn = /^([A-Za-z]+)[.,;](?=\s|$)/.exec(next);
    if (leadIn && LEASE_TERM.has(leadIn[1].toLowerCase())) return true;

    const match = /([A-Za-z][A-Za-z.]*)\.$/.exec(fragment);
    if (!match) return false;
    const token = match[1].toLowerCase();
    if (token.length === 1) return /(?:^|\s)[A-Z]\.$/.test(fragment);
    if (ALWAYS.has(token)) return true;
    return TRAILING.has(token) && /^[a-z0-9$(]/.test(next);
};

export const splitXThreadSentences = (text: string): string[] => {
    const parts = text.split(/(?<=[.?!])(\s+)(?=[A-Z0-9“‘])/);
    const sentences: string[] = [];
    let buffer = parts[0] ?? "";
    for (let i = 1; i < parts.length; i += 2) {
        const separator = parts[i];
        const next = parts[i + 1] ?? "";
        if (!separator.includes("\n") && periodContinues(buffer, next)) {
            buffer += separator + next;
        } else {
            sentences.push(buffer);
            buffer = next;
        }
    }
    sentences.push(buffer);
    return sentences.map((s) => s.trim()).filter(Boolean);
};

export const splitXThread = (text: string): string[] => {
    const body = text.replace(/\n{2,}/g, "\n").trim();

    const pieces: string[] = [];
    for (const sentence of splitXThreadSentences(body)) {
        let rest = sentence;
        while (rest.length > TWEET_LIMIT) {
            let idx = rest.lastIndexOf(" ", TWEET_LIMIT);
            if (idx === -1) idx = TWEET_LIMIT;
            pieces.push(rest.slice(0, idx).trim());
            rest = rest.slice(idx).trim();
        }
        if (rest) pieces.push(rest);
    }

    // Each line break goes out as two characters, so it counts twice.
    const threads: string[] = [];
    let current = "";
    for (const piece of pieces) {
        const candidate = current ? `${current}\n${piece}` : piece;
        if (candidate.length + (candidate.match(/\n/g) || []).length <= TWEET_LIMIT) {
            current = candidate;
        } else {
            if (current) threads.push(current);
            current = piece;
        }
    }
    if (current) threads.push(current);
    return threads;
};
